<template>
  <div class="ml-56 h-screen overflow-y-scroll">
    <!-- Loading - @TODO loading isn't used as org is always defined before enter -->
    <div
      v-if="isLoading"
      class="w-full h-screen flex items-center justify-center bg-truegray-100"
    >
      <div class="w-24 h-24 flex items-center justify-center">
        <lottie :options="getLoadingAnimationsOptions('dark')" />
        <span class="text-center">Loading organisation details</span>
      </div>
    </div>
    <!-- /Loading -->

    <!-- Settings content -->
    <div v-else class="h-full flex flex-wrap overflow-y-scroll">
      <!-- LHS -->
      <div class="relative w-full lg:w-6/12 bg-white pb-6 lg:pb-12 space-y-8">
        <!-- Page header -->
        <PageHeader
          :overline="headerConfig.overline"
          :title="headerConfig.title"
        />
        <!-- /Page header -->

        <!-- LHS content -->
        <div class="w-full px-12">
          <button
            :disabled="!canSubmit"
            type="button"
            class="btn btn-cw-cd-primary relative justify-self-end lg:hidden"
            :class="{
              'opacity-25 cursor-not-allowed': !canSubmit,
              'thinking': isSubmitting,
            }"
            @click="onSubmit"
          >
            <div
              class="absolute inset-0 w-full flex justify-center items-center z-40"
            >
              <lottie
                v-if="isSubmitting"
                :options="getLoadingAnimationsOptions()"
              />
            </div>
            <span :class="{ invisible: isSubmitting }"> Save changes </span>
          </button>

          <div class="grid grid-cols-1 gap-6">
            <!-- Organisation Name -->
            <div class="w-full">
              <label for="orgName" class="block text-md font-nunito">
                Organisation Name *
              </label>
              <input
                id="orgName"
                v-model="orgName"
                type="text"
                name="orgName"
                autocomplete="Organisation Name"
                :class="inputClasses"
                class="w-full md:w-3/4 xl:w-1/2 h-12"
              />
            </div>

            <!-- Organisation Website -->
            <div class="w-full">
              <label for="orgWebsite" class="block text-md font-nunito">
                Website
              </label>
              <input
                id="orgWebsite"
                v-model="orgWebsite"
                type="text"
                name="orgWebsite"
                autocomplete="Organisation Website"
                :class="inputClasses"
                class="w-full h-12"
              />
            </div>

            <!-- Organisation Description -->
            <div class="w-full">
              <label for="orgDescription" class="block text-md font-nunito">
                Description
              </label>
              <textarea
                id="orgDescription"
                v-model="orgDescription"
                type="text"
                name="orgDescription"
                autocomplete="Organisation Description"
                rows="8"
                :class="inputClasses"
                class="w-full p-4"
              />
            </div>
          </div>
        </div>
        <!-- /LHS content -->
      </div>
      <!-- /LHS -->

      <!-- RHS -->
      <div class="w-screen lg:w-6/12 bg-white lg:bg-truegray-100">
        <div
          class="w-full flex flex-col gap-8 pt-0 lg:pt-12 py-12 lg:py-20 px-12 lg:flex-col-reverse"
        >
          <!-- Organisation Logo -->
          <div class="w-full">
            <label for="orgLogoInput" class="block text-md font-nunito">
              Organisation Logo
            </label>
            <div class="mt-1 flex items-center mb-6">
              <div
                class="banner-changer w-full h-32 my-2 rounded bg-no-repeat bg-center bg-cover flex items-center justify-center shadow-sm relative z-20"
                :style="{ backgroundImage: `url(${orgLogo})` }"
              >
                <button
                  :disabled="isSubmitting"
                  type="button"
                  class="z-30 absolute right-1/2 bottom-1/2 transform translate-y-1/2 translate-x-1/2 btn btn-cw-cd-primary-trans m-0 ml-6"
                  :class="{ 'opacity-25 cursor-not-allowed': isSubmitting }"
                  @click="actionOrgLogo"
                >
                  {{ orgLogoButtonText }}
                </button>
              </div>
              <form ref="orgLogoForm">
                <input
                  ref="orgLogoInput"
                  type="file"
                  class="hidden"
                  @input="selectOrgLogo"
                />
              </form>
            </div>

            <!-- Logo preview -->
            <div class="rounded-xl bg-white p-0 lg:p-6">
              <label
                class="block text-md font-nunito border-b border-truegray-300 w-full pb-2"
              >
                Preview of your Logo
              </label>
              <div class="flex pt-4 px-8 items-center justify-center h-60">
                <div
                  v-if="orgLogo"
                  class="rounded-full flex items-center justify-center border-2 w-40 h-40 relative flex-shrink-0 contributor-badge-cw"
                >
                  <div
                    class="bg-no-repeat bg-cover bg-center w-40 h-40 rounded-full"
                    :style="{ backgroundImage: 'url(' + orgLogo + ')' }"
                  />

                  <div
                    class="absolute border-4 border-white cw-gradient -top-2.5 -right-2.5 h-10 w-10 rounded-full text-white"
                  >
                    <div class="flex items-center justify-center">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path
                          fill-rule="evenodd"
                          d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                          clip-rule="evenodd"
                        />
                      </svg>
                    </div>
                  </div>
                </div>
                <p v-else class="text-xs">
                  Update your organisation logo to preview
                </p>
              </div>
            </div>
            <!-- /Logo preview -->
          </div>

          <!-- Submit button -->
          <div class="hidden lg:flex justify-end">
            <button
              :disabled="!canSubmit"
              type="button"
              class="btn btn-cw-cd-primary relative"
              :class="{
                'opacity-25 cursor-not-allowed': !canSubmit,
                'thinking': isSubmitting,
              }"
              @click="onSubmit"
            >
              <div
                class="absolute inset-0 w-full flex justify-center items-center z-40"
              >
                <lottie
                  v-if="isSubmitting"
                  :options="getLoadingAnimationsOptions()"
                />
              </div>
              <span :class="{ invisible: isSubmitting }"> Save changes </span>
            </button>
          </div>
          <!-- /Submit button -->

          <!-- Social media -->
          <!--
            <div class="rounded-xl bg-white p-6">
            <label
            class="block text-md font-nunito border-b border-truegray-300 w-full pb-2"
            >
            Social Media
            </label>
            <div class="flex flex-col pt-4 px-8 items-center justify-center">
            </div>
            </div> 
          -->
          <!-- /Social media -->
        </div>
      </div>
      <!-- /RHS -->

      <div
        v-if="hasPublicProfile"
        class="w-full bg-white border-t p-12 flex flex-col gap-6"
      >
        <h2
          class="uppercase text-cw-700 font-nunito font-light text-lg tracking-widest"
        >
          Public Profile Settings
        </h2>

        <div class="grid grid-cols-1 lg:grid-cols-5 gap-x-12 gap-y-8">
          <div class="grid grid-cols-1 lg:col-span-2 gap-6">
            <!-- Email -->
            <div class="col-span-1">
              <label class="flex items-center text-md font-nunito">
                Public email
              </label>
              <input
                id="email"
                v-model="email"
                type="email"
                name="email"
                :class="inputClasses"
                class="w-full h-12"
              />
            </div>
            <!-- /Email -->

            <!-- Phone -->
            <div class="col-span-1">
              <label class="flex items-center text-md font-nunito">
                Public phone number
              </label>
              <input
                id="phoneNumber"
                v-model="phoneNumber"
                type="number"
                name="phoneNumber"
                :class="inputClasses"
                class="w-full h-12"
              />
            </div>
            <!-- /Phone -->
          </div>

          <div class="col-span-1 lg:col-span-3 flex flex-col gap-5">
            <div class="w-full flex flex-col gap-1">
              <label for="url">Profile URL</label>
              <a
                id="url"
                :href="organisation.public_profile_url"
                target="_blank"
                class="w-max text-cw-600 hover:text-cw-alt font-nunito"
              >
                {{ organisation.public_profile_url }}
              </a>
            </div>

            <div class="flex flex-col gap-2">
              <label class="flex items-center text-md font-nunito">
                Primary colour *
              </label>
              <TwitterPicker
                v-if="colourPrimary"
                id="colourPrimary"
                v-model="colourPrimary"
                name="colourPrimary"
                :triangle="'hide'"
                :default-colors="[
                  '#E66E00',
                  '#A10F20',
                  '#E5287C',
                  '#B639D6',
                  '#5C25D0',
                  '#12268E',
                  '#1860D4',
                  '#3B82F6',
                  '#619709',
                  '#0F8C7D',
                ]"
              />
            </div>
          </div>
        </div>

        <hr class="w-full my-3" />

        <!-- Hero image -->
        <div class="grid grid-cols-1 lg:grid-cols-5 gap-x-12 gap-y-8 relative">
          <div class="flex flex-col w-full col-span-1 lg:col-span-2">
            <form ref="bannerHeroForm">
              <input
                ref="bannerHeroInput"
                type="file"
                class="hidden"
                @input="selectImage(BANNER_HERO_CONFIG_MAP)"
              />
            </form>

            <label
              for="bannerHero"
              class="flex items-center text-md font-nunito"
            >
              Hero image *
            </label>
            <div class="flex items-center mt-2">
              <div
                class="banner-changer w-full h-32 rounded bg-no-repeat bg-center bg-cover flex items-center justify-center shadow-sm relative z-20"
                :style="{
                  backgroundImage: `url(${bannerHeroPreviewUrl})`,
                }"
              >
                <button
                  type="button"
                  class="z-30 absolute right-1/2 bottom-1/2 transform translate-y-1/2 translate-x-1/2 btn btn-trans-white text-white m-0 ml-6"
                  @click="updateImage(BANNER_HERO_CONFIG_MAP)"
                >
                  {{ getImageText(BANNER_HERO_CONFIG_MAP) }}
                </button>
              </div>
            </div>
            <div class="text-xs text-truegray-500 mt-1">
              .jpg or .png format with a maximum file size of 2 MB
            </div>
          </div>

          <div v-if="bannerHeroPreviewUrl" class="col-span-1 lg:col-span-3">
            <template>
              <label class="flex items-center text-md font-nunito">
                Hero image preview
              </label>
              <div
                class="w-full h-52 rounded bg-no-repeat bg-center bg-cover flex items-center justify-center shadow-sm z-20 mt-2"
                :style="{
                  backgroundImage: `url(${bannerHeroPreviewUrl})`,
                }"
              />
            </template>
          </div>
        </div>
        <!-- /Hero image -->

        <hr class="w-full my-3" />

        <!-- Bio -->
        <div class="grid grid-cols-1 lg:grid-cols-5 gap-x-12 gap-y-8">
          <!-- Bio -->
          <div class="flex flex-col w-full col-span-1 lg:col-span-2">
            <label class="flex items-center text-md font-nunito">
              Profile bio *
            </label>
            <Editor
              :key="organisation.id"
              :initial-content="organisation.profile_bio"
              :active-features="[
                'headings',
                'bold',
                'italic',
                'strike',
                'underline',
                'h1',
                'h2',
                'h3',
                'alignments',
                'alignLeft',
                'alignCenter',
                'alignRight',
                'lists',
                'bulletList',
                'orderedList',
                'link',
                'remove_link',
                'horizontalRule',
                'undo',
                'redo',
              ]"
              class="mt-2"
              editor-class="max-h-[200px] overflow-y-scroll"
              @update-content="updateBio"
            />
          </div>

          <div v-if="hasBio" class="col-span-1 lg:col-span-3">
            <label class="flex items-center text-md font-nunito">
              Bio preview
            </label>
            <div
              class="prose cw__profile-bio rounded-3xl border-8 p-6 mt-2 overflow-y-scroll break-words"
              :style="{
                borderColor: colourPrimary?.hex,
                maxHeight: '280px',
              }"
              v-html="bio"
            />
          </div>
        </div>
        <!-- /Bio -->

        <hr class="w-full my-3" />

        <!-- Secondary image -->
        <div class="grid col-span-1 lg:grid-cols-5 gap-x-12 gap-y-8 relative">
          <div class="flex flex-col w-full col-span-1 lg:col-span-2">
            <form ref="bannerSecondaryForm">
              <input
                ref="bannerSecondaryInput"
                type="file"
                class="hidden"
                @input="selectImage(BANNER_SECONDARY_CONFIG_MAP)"
              />
            </form>

            <label
              for="bannerSecondary"
              class="flex items-center text-md font-nunito"
            >
              Secondary image
            </label>
            <div class="flex items-center mt-2">
              <div
                class="banner-changer w-full h-32 rounded bg-no-repeat bg-center bg-cover flex items-center justify-center shadow-sm relative z-20"
                :style="{
                  backgroundImage: `url(${bannerSecondaryPreviewUrl})`,
                }"
              >
                <button
                  type="button"
                  class="z-30 absolute right-1/2 bottom-1/2 transform translate-y-1/2 translate-x-1/2 btn btn-trans-white text-white m-0 ml-6"
                  @click="updateImage(BANNER_SECONDARY_CONFIG_MAP)"
                >
                  {{ getImageText(BANNER_SECONDARY_CONFIG_MAP) }}
                </button>
              </div>
            </div>
            <div class="text-xs text-truegray-500 mt-1">
              .jpg or .png format with a maximum file size of 2 MB
            </div>
          </div>

          <div :class="['col-span-1 lg:col-span-3']">
            <template v-if="bannerSecondaryPreviewUrl">
              <label class="flex items-center text-md font-nunito">
                Secondary image preview
              </label>
              <div
                class="w-full h-52 rounded bg-no-repeat bg-center bg-cover flex items-center justify-center shadow-sm z-20 mt-2"
                :style="{
                  backgroundImage: `url(${bannerSecondaryPreviewUrl})`,
                }"
              />
            </template>
          </div>

          <div
            class="col-span-1 lg:col-span-3 flex flex-row gap-3 items-center"
          >
            <input
              id="bannerSecondaryRemove"
              v-model="bannerSecondaryRemove"
              type="checkbox"
              name="bannerSecondaryRemove"
              class="h-5 w-5 cursor-pointer"
            />
            <label
              for="bannerSecondaryRemove"
              class="flex items-center text-md font-nunito"
            >
              Remove secondary image
            </label>
          </div>
        </div>
        <!-- /Secondary banner -->

        <hr class="w-full my-3" />

        <!-- Video -->
        <div
          v-if="videoType"
          class="grid grid-cols-1 lg:grid-cols-5 gap-x-12 gap-y-8"
        >
          <div class="flex flex-col gap-4 w-full col-span-1 lg:col-span-2">
            <div class="flex flex-col w-full">
              <label class="flex items-center text-md font-nunito mb-3">
                Profile video
              </label>
              <div class="flex relative mb-5">
                <button
                  type="button"
                  class="inline-flex items-center cursor-pointer mr-4 text-p"
                  @click="setVideoType(VIDEO_TYPES[0])"
                >
                  <span :class="[VIDEO_TYPES[0].class, 'ml-1']">
                    {{ VIDEO_TYPES[0].value }}
                  </span>
                </button>

                <div class="w-px relative top-0.5 h-4 bg-truegray-300 mr-4" />

                <button
                  type="button"
                  class="inline-flex items-center cursor-pointer mr-4 text-p"
                  @click="setVideoType(VIDEO_TYPES[1])"
                >
                  <span :class="[VIDEO_TYPES[1].class, 'ml-1']">
                    {{ VIDEO_TYPES[1].value }}
                  </span>
                </button>

                <div
                  :class="[
                    videoType.underlineClass,
                    'absolute h-1 left-1 top-full transform translate-y-2 w-16 transition-all duration-300 ease-in-out rounded',
                  ]"
                />
              </div>
            </div>

            <div>
              <label
                for="videoCode"
                class="flex items-center text-md font-nunito"
              >
                {{ videoType.value }} video ID
                <div class="relative group z-10">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    class="h-3 w-3 ml-2 cursor-pointer"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fill-rule="evenodd"
                      d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                      clip-rule="evenodd"
                    />
                  </svg>
                  <div
                    class="absolute left-6 top-0 w-64 z-50 border border-truegray-300 rounded p-4 opacity-0 transition-all duration-300 transform pointer-events-none shadow group-hover:opacity-100 bg-white text-sm"
                  >
                    Only insert the {{ videoType.value }} video ID number or
                    code. For example:
                    <span class="text-truegray-500 italic">
                      {{ videoType.exampleUrl
                      }}<span :class="videoType.class">{{
                        videoType.exampleCode
                      }}</span>
                    </span>
                  </div>
                </div>
              </label>

              <input
                id="videoCode"
                v-model="videoCode"
                type="text"
                name="videoCode"
                :class="inputClasses"
                class="w-full h-12"
              />
            </div>
          </div>

          <div class="col-span-1 lg:col-span-3">
            <template v-if="videoPreviewSrc">
              <label class="flex items-center text-md font-nunito">
                Video preview
              </label>
              <iframe
                type="iframe"
                class="rounded-3xl w-full shadow-lg mt-2"
                :src="videoPreviewSrc"
                frameborder="0"
                height="300"
              />
            </template>
          </div>
        </div>
        <!-- /Video -->
      </div>
    </div>
    <!-- /Settings content -->
  </div>
</template>

<script>
import { defineComponent, computed, ref, onMounted, watch } from 'vue'
import { useStore } from '../../../js/store/index.js'
import PageHeader from '../components/PageHeader.vue'
import Editor from '../../../careerwise/tenant/manager/components/editor-wysiwyg/Editor.vue'
import * as loadingAnimation from '../../../shared/components/loading.json'
import * as loadingAnimationDark from '../../../shared/components/loading-dark.json'
import { Twitter } from 'vue-color'
import { validateImageFormat } from '../../../js/util/helper.js'

export default defineComponent({
  name: 'Settings',

  components: {
    PageHeader,
    Editor,
    TwitterPicker: Twitter,
  },

  setup(_, { emit }) {
    const store = useStore()

    const isLoading = ref(false)
    const isSubmitting = ref(false)
    const getLoadingAnimationsOptions = (type) => {
      const animation =
        type === 'dark' ? loadingAnimationDark : loadingAnimation
      return {
        animationData: animation.default,
      }
    }
    const headerConfig = {
      overline: 'settings',
      title: 'Organisation Details',
    }
    const inputClasses =
      'flex justify-center items-center sm:text-sm text-truegray-700 focus:text-black active:text-black focus:outline-none active:outline-none transition-all duration-100 ease-in-out border border-truegray-300 focus:border-black active:border-black rounded bg-white px-4 mt-2'

    // Organisation
    const organisation = computed(
      () => store.getters['contributors/getOrganisation'],
    )
    const organisationId = computed(
      () => store.getters['contributors/getOrganisationId'],
    )

    const hasPublicProfile = computed(
      () => organisation.value?.public_profile_active,
    )

    // Org name
    const orgName = ref(null)
    const orgNameOriginal = computed(() => organisation.value?.title || null)
    const orgNameUpdated = computed(() =>
      hasBeenUpdated(orgName.value, orgNameOriginal.value),
    )

    // Org logo
    const orgLogo = ref(null)
    const orgLogoOriginal = computed(() => organisation.value?.logo || null)
    const orgLogoUpdated = computed(() =>
      hasBeenUpdated(orgLogo.value, orgLogoOriginal.value),
    )
    const orgLogoForm = ref(null)
    const orgLogoInput = ref(null)
    const orgLogoButtonText = computed(() => {
      return orgLogoUpdated.value
        ? 'Cancel'
        : orgLogoOriginal.value
        ? 'Edit logo'
        : 'Add logo'
    })

    const actionOrgLogo = () => {
      if (orgLogoUpdated.value) {
        // Cancelling
        orgLogoForm.value.reset()
        orgLogo.value = orgLogoOriginal.value || null
      } else {
        // Editing
        orgLogoInput.value.click()
      }
    }

    const selectOrgLogo = () => {
      const file = orgLogoInput.value.files
      if (file && file[0]) {
        if (!validateImageFormat(file[0])) {
          return
        }
      }

      const reader = new FileReader()

      orgLogo.value = file[0]
      reader.onload = (e) => {
        orgLogo.value = e.target.result
      }

      reader.readAsDataURL(file[0])
      emit('input', file[0])
    }

    // Org website
    const orgWebsite = ref(null)
    const orgWebsiteOriginal = computed(
      () => organisation.value?.website || null,
    )
    const orgWebsiteUpdated = computed(() =>
      hasBeenUpdated(orgWebsite.value, orgWebsiteOriginal.value),
    )

    // Org description
    const orgDescription = ref(null)
    const orgDescriptionOriginal = computed(
      () => organisation.value?.description || null,
    )
    const orgDescriptionUpdated = computed(() =>
      hasBeenUpdated(orgDescription.value, orgDescriptionOriginal.value),
    )

    // Public profile fields
    // Bio
    const bio = ref(null)
    const hasBio = computed(() => {
      const strippedContent = bio.value?.replace(/<[^>]*>?/gm, '')
      return !!strippedContent?.trim()
    })
    const updateBio = (val) => {
      bio.value = val
    }

    // Email/phone
    const email = ref(null)
    const phoneNumber = ref(null)

    // Primary colour
    const colourPrimary = ref(null)

    // Video
    const VIDEO_TYPES = [
      {
        value: 'YouTube',
        class: 'font-semibold text-expo_red',
        underlineClass: 'bg-expo_red translate-x-0',
        exampleUrl: 'https://www.youtube.com/watch?v=',
        exampleCode: 'GCHLt0NDkdY',
        urlPath: 'https://www.youtube.com/embed/',
      },
      {
        value: 'Vimeo',
        class: 'font-semibold text-cyan-400',
        underlineClass: 'bg-cyan-400 translate-x-130',
        exampleUrl: 'https://vimeo.com/',
        exampleCode: '108799588',
        urlPath: 'https://player.vimeo.com/video/',
      },
    ]
    const videoType = ref(null)
    const videoCode = ref(null)
    const videoPreviewSrc = computed(() => {
      if (videoCode.value && videoType.value) {
        return `${videoType.value.urlPath}${videoCode.value}`
      }

      return null
    })

    const setVideoType = (type) => {
      videoType.value = type
    }

    // Images
    const bannerHero = ref(null)
    const bannerHeroForm = ref(null)
    const bannerHeroInput = ref(null)
    const bannerHeroPreviewUrl = ref(null)
    const bannerHeroPreviewChanged = ref(false)

    const BANNER_HERO_CONFIG_MAP = {
      hasChanged: bannerHeroPreviewChanged,
      inputValue: bannerHero,
      organisationValue: 'profile_image_hero',
      organisationValueRemove: 'profile_image_hero_remove',
      previewUrl: bannerHeroPreviewUrl,
      inputForm: bannerHeroForm,
      inputRef: bannerHeroInput,
    }

    const bannerSecondary = ref(null)
    const bannerSecondaryForm = ref(null)
    const bannerSecondaryInput = ref(null)
    const bannerSecondaryPreviewUrl = ref(null)
    const bannerSecondaryPreviewChanged = ref(false)
    const bannerSecondaryRemove = ref(false)

    const BANNER_SECONDARY_CONFIG_MAP = {
      hasChanged: bannerSecondaryPreviewChanged,
      inputValue: bannerSecondary,
      organisationValue: 'profile_image_secondary',
      inputValueRemove: bannerSecondaryRemove,
      organisationValueRemove: 'profile_image_secondary_remove',
      previewUrl: bannerSecondaryPreviewUrl,
      inputForm: bannerSecondaryForm,
      inputRef: bannerSecondaryInput,
    }

    // Image handlers
    const IMAGE_CONFIG_ARR = [
      BANNER_HERO_CONFIG_MAP,
      BANNER_SECONDARY_CONFIG_MAP,
    ]

    const updateImage = (config) => {
      resetImageRemoved(config)
      if (config.hasChanged.value) {
        // Cancel
        resetImage(config)
      } else {
        // Update
        config.inputRef.value.click()
      }
    }

    const resetImage = (config, setPreviewNull = false) => {
      config.hasChanged.value = false
      config.inputValue.value = null
      config.previewUrl.value = setPreviewNull
        ? null
        : organisation.value[config.organisationValue] || ''
      config.inputForm.value.reset()
    }

    const resetImageRemoved = (config) => {
      if (config.inputValueRemove) {
        config.inputValueRemove.value = false
      }
    }

    const getImageText = (config) => {
      return config.hasChanged.value ? 'Cancel' : 'Change'
    }

    const selectImage = (config) => {
      const files = config.inputRef.value.files
      const file = files?.[0]
      if (file) {
        if (!validateImageFormat(file)) {
          return
        }
      }

      const reader = new FileReader()
      config.inputValue.value = file

      reader.onload = (e) => {
        config.hasChanged.value = true
        // config.inputValueRemove.value = false
        config.previewUrl.value = e.target.result
      }

      reader.readAsDataURL(file)
    }

    // Submission
    const hasBeenUpdated = (item, itemOriginal) => {
      return item !== itemOriginal
    }

    const canSubmit = computed(() => {
      let publicProfileValid = true

      if (hasPublicProfile.value) {
        // Required public fields
        publicProfileValid =
          !!colourPrimary.value &&
          !!bannerHeroPreviewUrl.value &&
          !!hasBio.value
      }

      return !isSubmitting.value && publicProfileValid && orgName.value
    })

    watch(bannerSecondaryRemove, (val) => {
      resetImage(BANNER_SECONDARY_CONFIG_MAP, val)
    })

    const onSubmit = async () => {
      if (!canSubmit.value) {
        return
      }

      const formData = new FormData()

      // Append updated values
      formData.append('title', orgName.value)
      formData.append('website', orgWebsite.value)
      formData.append('description', orgDescription.value)
      // Only append images if they have changed!
      if (orgLogoUpdated.value) {
        formData.append('logo', orgLogoInput.value.files[0])
      }

      // Public profile values
      if (hasPublicProfile.value) {
        const fields = [
          { key: 'profile_bio', value: bio.value },
          { key: 'public_email', value: email.value },
          { key: 'public_phone', value: phoneNumber.value },
          { key: 'profile_colour', value: colourPrimary.value?.hex },
          { key: 'video_code', value: videoCode.value },
          // Add videoType as an empty string (null) if code is not set
          {
            key: 'video_type',
            value: videoCode.value ? videoType.value?.value : '',
          },
          {
            key: BANNER_SECONDARY_CONFIG_MAP.organisationValueRemove,
            value: BANNER_SECONDARY_CONFIG_MAP.inputValueRemove.value,
          },
        ]

        fields.forEach((field) => {
          formData.append(field.key, field.value || '')
        })

        // Handle images - only append them if they have changed!
        IMAGE_CONFIG_ARR.forEach((config) => {
          if (config.hasChanged.value) {
            formData.append(
              config.organisationValue,
              config.inputValue.value || '',
            )
          }
        })
      }

      isSubmitting.value = true
      await store.dispatch('contributors/dispatchEditOrganisation', formData)
      isSubmitting.value = false
      await init()
    }

    watch(organisationId, () => init())

    // Init
    // Note: empty strings because form data converts null to "null"
    // in the payload.
    const init = async () => {
      isLoading.value = true
      orgName.value = orgNameOriginal.value || ''
      orgLogo.value = orgLogoOriginal.value || ''
      orgWebsite.value = orgWebsiteOriginal.value || ''
      orgDescription.value = orgDescriptionOriginal.value || ''

      // Public profile details
      if (hasPublicProfile.value) {
        bio.value = organisation.value.profile_bio || ''
        email.value = organisation.value.public_email || ''
        phoneNumber.value = organisation.value.public_phone || ''
        colourPrimary.value = {
          hex: organisation.value.profile_colour || '#ff4778',
        }
        videoCode.value = organisation.value.video_code || ''
        videoType.value =
          VIDEO_TYPES.find(
            (type) => type.value == organisation.value.video_type,
          ) || VIDEO_TYPES[0]

        IMAGE_CONFIG_ARR.forEach((config) => {
          resetImageRemoved(config)
          resetImage(config)
        })
      }

      isLoading.value = false
    }

    onMounted(init)

    return {
      getLoadingAnimationsOptions,
      inputClasses,
      headerConfig,

      isLoading,
      isSubmitting,
      canSubmit,

      organisation,
      hasPublicProfile,

      orgName,
      orgLogo,
      orgLogoForm,
      orgLogoInput,
      orgLogoButtonText,
      selectOrgLogo,
      actionOrgLogo,
      orgWebsite,
      orgDescription,

      bio,
      hasBio,
      updateBio,

      email,
      phoneNumber,
      colourPrimary,

      bannerHero,
      bannerHeroForm,
      bannerHeroInput,
      bannerHeroPreviewUrl,
      bannerHeroPreviewChanged,
      BANNER_HERO_CONFIG_MAP,

      bannerSecondary,
      bannerSecondaryForm,
      bannerSecondaryInput,
      bannerSecondaryPreviewUrl,
      bannerSecondaryPreviewChanged,
      bannerSecondaryRemove,
      BANNER_SECONDARY_CONFIG_MAP,

      updateImage,
      resetImage,
      getImageText,
      selectImage,

      VIDEO_TYPES,
      videoType,
      videoCode,
      videoPreviewSrc,
      setVideoType,

      onSubmit,
    }
  },
})
</script>

<style scoped>
.banner-changer::after {
  background: #000;
  opacity: 0.6;
  content: '';
  width: 100%;
  height: 100%;
  position: absolute;
  left: 0;
  top: 0;
  border-radius: 0.25rem;
}

.contributor-badge-cw::before {
  top: -20px;
  left: -20px;
  right: -20px;
  bottom: -20px;
  border-radius: 100%;
  border: 10px solid transparent;
}
</style>
